/**
 * Typed fetch client for ads-api.
 * Session travels in the `ads_session` HttpOnly cookie (rest-api.md §Seguridad transversal);
 * every mutating request carries `X-CSRF-Token` (double submit). El token llega en la cabecera `X-CSRF-Token` de
 * `GET /auth/me` y se guarda SOLO en memoria (contracts/panel-api.md §0, T095): la cookie `ads_csrf` pasa a
 * `Path=/api` y desde `/c/*` ya no se puede leer con `document.cookie`. Mientras el servidor no mande la cabecera,
 * se lee la cookie como hasta ahora. Responses are parsed through Zod before use.
 */
import { z } from "zod";
import { getAdsBasePath } from "@/utils/basePath";

// HTTP input is unknown; T is the validated OUTPUT, which can differ after
// a schema transform (e.g. a wire series normalized for the chart).
type ZodSchema<T> = z.ZodType<T, z.ZodTypeDef, unknown>;

// 026 (contracts/cockpit-read-model.md §5): bajo el puente de sesión de
// Safent el documento se sirve en `/ads/...`, así que las llamadas a la API
// también deben llevar ese prefijo -- `getAdsBasePath()` lee lo que
// `composition/app.py` inyectó en `index.html` ("" fuera del modo empotrado).

/** Para respuestas `204 No Content` sin cuerpo (logout, login, unpair…). */
export const voidResponseSchema = z.undefined();
const CSRF_COOKIE_NAME = "ads_csrf";

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export class ApiSchemaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ApiSchemaError";
  }
}

let unauthorizedListeners: Array<() => void> = [];

/** Fallo de contexto de cliente que el servidor rechaza sin efecto (contracts/panel-api.md §1.4). */
export type ClientFault = "context-required" | "mismatch";
let clientFaultListeners: Array<(fault: ClientFault) => void> = [];

/** Suscripción a `400 CLIENT_CONTEXT_REQUIRED` y `409 CLIENT_MISMATCH`: el marco avisa a la persona. */
export function onClientFault(listener: (fault: ClientFault) => void): () => void {
  clientFaultListeners.push(listener);
  return () => {
    clientFaultListeners = clientFaultListeners.filter((l) => l !== listener);
  };
}

// Cliente que hay en pantalla. Solo es el respaldo de las peticiones que no nombran a su cliente
// (`business_id` en consulta o cuerpo, o `clientId`): las que sí lo nombran llevan SIEMPRE el suyo,
// aunque se lancen después de cambiar de cliente.
let activeClientId: string | null = null;
const SAFE_CLIENT_ID = /^[A-Za-z0-9_-]{1,64}$/;

export function setActiveClientId(clientId: string | null): void {
  activeClientId = clientId && SAFE_CLIENT_ID.test(clientId) ? clientId : null;
}

// Rutas `session` y `agency` (contracts/panel-api.md §1.2): `X-Client-Id` está prohibida en ellas.
const CLIENTLESS_PATHS = [/^\/auth(\/|$)/, /^\/agency(\/|$)/, /^\/clients$/, /^\/clients\/slug-availability$/, /^\/platform-apps(\/|$)/,
  /^\/integrations\/cloudflare(\/|$)/, /^\/mcp-oauth(\/|$)/, /^\/onboarding(\/|$)/, /^\/ads-catalog(\/|$)/, /^\/decision-log\/verify$/];
const IDEMPOTENT_POST_PATHS = [/^\/whatsapp\/conversations\/[^/]+\/messages$/];

function bodyBusinessId(body: unknown): string | undefined {
  if (typeof body !== "object" || body === null || !("business_id" in body)) return undefined;
  const value = (body as { business_id: unknown }).business_id;
  return typeof value === "string" ? value : undefined;
}

function clientHeaders(method: string, path: string, options: Pick<RequestOptions, "query" | "body" | "clientId" | "headers">): Record<string, string> {
  const headers: Record<string, string> = {};
  if (!CLIENTLESS_PATHS.some((pattern) => pattern.test(path))) {
    const queryId = options.query?.business_id;
    const candidate = options.clientId ?? (typeof queryId === "string" ? queryId : undefined) ?? bodyBusinessId(options.body) ?? activeClientId;
    if (candidate && SAFE_CLIENT_ID.test(candidate)) headers["X-Client-Id"] = candidate;
  }
  const hasKey = Object.keys(options.headers ?? {}).some((name) => name.toLowerCase() === "idempotency-key");
  if (method === "POST" && !hasKey && IDEMPOTENT_POST_PATHS.some((pattern) => pattern.test(path))) {
    headers["Idempotency-Key"] = crypto.randomUUID();
  }
  return headers;
}
// Tras la primera 401 de sesión caducada, el resto de peticiones fallan al instante (sin red) hasta que
// el propietario entra de nuevo: evita una cascada de 401 y de redirecciones a /login.
let sessionExpired = false;
const LOGIN_ALLOWED_AFTER_EXPIRY = new Set(["POST /auth/login", "POST /auth/federated/start", "GET /auth/federated/status"]);

/** Subscribes to 401 responses (session expired/absent). Returns an unsubscribe function. */
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.push(listener);
  return () => {
    unauthorizedListeners = unauthorizedListeners.filter((l) => l !== listener);
  };
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  const value = match?.[1];
  return value ? decodeURIComponent(value) : null;
}

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const CSRF_HEADER = "X-CSRF-Token";
// Forma de un token legítimo: se descarta cualquier otra cosa antes de reenviarla en una cabecera.
const CSRF_SHAPE = /^[A-Za-z0-9._~+/=-]{8,512}$/;
// Rutas públicas: no hay sesión de la que pedir el token (pedirlo daría 401 y marcaría la sesión como caducada).
const CSRF_PUBLIC_PATHS = [/^\/auth\/login$/, /^\/auth\/federated(\/|$)/, /^\/auth\/invitations(\/|$)/];

// Solo en memoria: nunca localStorage ni sessionStorage.
let csrfInMemory: string | null = null;
// `/auth/me` ya se leyó sin cabecera ni cookie: no se vuelve a preguntar antes de cada mutación.
let csrfUnavailable = false;
let csrfRenewal: Promise<void> | null = null;

function rememberCsrf(response: Response): void {
  const value = response.headers.get(CSRF_HEADER);
  if (value && CSRF_SHAPE.test(value)) {
    csrfInMemory = value;
    csrfUnavailable = false;
  }
}

/** El token se pierde con la sesión (401, cierre de sesión): el siguiente `/auth/me` trae el nuevo. */
export function forgetCsrfToken(): void {
  csrfInMemory = null;
  csrfUnavailable = false;
}

/** Vuelve a leer `/auth/me` solo para renovar el token; si falla no pasa nada, la mutación decidirá. Una sola lectura a la vez. */
function renewCsrf(): Promise<void> {
  csrfRenewal ??= fetch(buildUrl("/auth/me"), { method: "GET", credentials: "include", cache: "no-store" })
    .then((response) => {
      rememberCsrf(response);
      if (!csrfInMemory && !readCookie(CSRF_COOKIE_NAME)) csrfUnavailable = true;
    })
    .catch(() => {
      csrfUnavailable = true;
    })
    .finally(() => {
      csrfRenewal = null;
    });
  return csrfRenewal;
}

/**
 * El token para una mutación: el de memoria (cabecera de `/auth/me`), si no el de la cookie (transitorio) y, si no hay
 * ninguno, se pide a `/auth/me` una vez antes de enviar.
 */
export async function csrfTokenForMutation(path = ""): Promise<string | null> {
  const available = () => csrfInMemory ?? readCookie(CSRF_COOKIE_NAME);
  const known = available();
  if (known || csrfUnavailable || CSRF_PUBLIC_PATHS.some((pattern) => pattern.test(path))) return known;
  await renewCsrf();
  return available();
}

async function isCsrfFailure(response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  const payload: unknown = await response.clone().json().catch(() => null);
  return isApiErrorPayload(payload) && /CSRF/i.test(payload.error.code);
}

/**
 * Envía una mutación con su token. Si el servidor la rechaza por CSRF (el token de memoria caducó), se renueva con
 * `/auth/me` y se reintenta UNA vez; un segundo rechazo se devuelve tal cual.
 */
async function sendWithCsrf(method: string, path: string, headers: Record<string, string>, send: (headers: Record<string, string>) => Promise<Response>): Promise<Response> {
  if (!MUTATING_METHODS.has(method)) return send(headers);
  const token = await csrfTokenForMutation(path);
  const response = await send(token ? { ...headers, [CSRF_HEADER]: token } : headers);
  if (!(await isCsrfFailure(response))) return response;
  csrfInMemory = null;
  await renewCsrf();
  const renewed = csrfInMemory ?? readCookie(CSRF_COOKIE_NAME);
  return renewed ? send({ ...headers, [CSRF_HEADER]: renewed }) : response;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Cabeceras extra, p.ej. `X-Action-Confirmation` (intención exacta) en mutaciones sensibles. */
  headers?: Record<string, string>;
  /** Cliente de la acción cuando la ruta no lo lleva en consulta ni cuerpo (`X-Client-Id`). */
  clientId?: string;
  /** Cuerpo binario (subida del logo): se envía tal cual, con su propio `Content-Type`. */
  binary?: Blob;
  signal?: AbortSignal;
}

/** URL absoluta de la API (enlaces de descarga que abre el navegador, fuera de `fetch`). */
export function apiUrl(path: string, query?: RequestOptions["query"]): string {
  return buildUrl(path, query);
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  // Embedded requests always use the authenticated same-origin bridge.
  const prefix = getAdsBasePath();
  const API_BASE = prefix ? `${prefix}/api/v1` : (import.meta.env.VITE_API_BASE ?? "/api/v1");
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function finishResponse<T>(path: string, schema: ZodSchema<T>, response: Response): Promise<T> {
  if (response.status === 204) {
    if (path === "/auth/login") sessionExpired = false;
    return schema.parse(undefined);
  }

  const payload: unknown = await response.json().catch(() => null);

  if (response.status === 401) {
    const code = isApiErrorPayload(payload) ? payload.error.code : "UNAUTHORIZED";
    // `REAUTH_REQUIRED` (prueba de presencia fresca de una mutación sensible, TOTP o
    // identificación federada — `fresh_identification.py`) no es una sesión caducada: NO
    // dispara la redirección global a `/login` — el llamador (`useFreshIdentification`) lo
    // muestra inline en el propio `FreshIdentificationPrompt` para que el propietario pueda
    // reintentar sin perder el resto del formulario ni la sesión.
    if (code === "REAUTH_REQUIRED") {
      const message = isApiErrorPayload(payload) ? payload.error.message : "Código de verificación no válido.";
      const details = isApiErrorPayload(payload) ? payload.error.details : undefined;
      throw new ApiRequestError(message, code, 401, details);
    }
    // Un 401 con otro código (p. ej. credenciales incorrectas en el login) no es una sesión caducada.
    if (code !== "UNAUTHORIZED") {
      const message = isApiErrorPayload(payload) ? payload.error.message : "No se pudo completar la autenticación.";
      throw new ApiRequestError(message, code, 401);
    }
    forgetCsrfToken();
    if (!sessionExpired) {
      sessionExpired = true;
      unauthorizedListeners.forEach((listener) => listener());
    }
    throw new ApiRequestError("Sesión no válida.", "UNAUTHORIZED", 401);
  }

  if (!response.ok) {
    const code = isApiErrorPayload(payload) ? payload.error.code : "UNKNOWN_ERROR";
    if (response.status === 400 && code === "CLIENT_CONTEXT_REQUIRED") {
      clientFaultListeners.forEach((listener) => listener("context-required"));
      throw new ApiRequestError("Recarga la página.", code, 400);
    }
    if (response.status === 409 && code === "CLIENT_MISMATCH") {
      clientFaultListeners.forEach((listener) => listener("mismatch"));
      throw new ApiRequestError("La acción era de otro cliente y no se ha hecho.", code, 409);
    }
    const message = isApiErrorPayload(payload) ? payload.error.message : "Algo ha fallado en el servidor.";
    const details = isApiErrorPayload(payload) ? payload.error.details : undefined;
    throw new ApiRequestError(message, code, response.status, details);
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiSchemaError(`Respuesta de ${path} no coincide con el contrato: ${parsed.error.message}`);
  }
  if (path === "/auth/login") sessionExpired = false;
  return parsed.data;
}

/** Solo para pruebas: cada prueba arranca con la sesión «viva», como una página recién cargada. */
export function resetSessionExpiredForTests(): void {
  sessionExpired = false;
  forgetCsrfToken();
}

function rejectIfSessionExpired(method: string, path: string): void {
  if (sessionExpired && !LOGIN_ALLOWED_AFTER_EXPIRY.has(`${method} ${path}`)) {
    throw new ApiRequestError("Sesión no válida.", "UNAUTHORIZED", 401);
  }
}

async function request<T>(path: string, schema: ZodSchema<T>, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  rejectIfSessionExpired(method, path);
  const headers: Record<string, string> = { ...clientHeaders(method, path, options), ...options.headers };
  if (options.binary) headers["Content-Type"] = options.binary.type;
  else if (options.body !== undefined) headers["Content-Type"] = "application/json";
  const body = options.binary ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined);

  const response = await sendWithCsrf(method, path, headers, (sent) =>
    fetch(buildUrl(path, options.query), { method, headers: sent, credentials: "include", body, signal: options.signal }),
  );
  if (path === "/auth/me") rememberCsrf(response);

  return finishResponse(path, schema, response);
}

export interface Download {
  blob: Blob;
  /** Nombre que propone el servidor en `Content-Disposition`, ya saneado; `null` si no lo manda. */
  filename: string | null;
}

const SAFE_FILENAME = /^[A-Za-z0-9._-]{1,120}$/;

function filenameFrom(response: Response): string | null {
  const match = /filename="?([^";]+)"?/i.exec(response.headers.get("Content-Disposition") ?? "");
  const name = match?.[1]?.trim() ?? "";
  return SAFE_FILENAME.test(name) && !name.startsWith(".") ? name : null;
}

/** Descarga un fichero que genera el servidor (CSV, informe): el cuerpo vuelve como `Blob`. */
async function download(path: string, options: RequestOptions = {}): Promise<Download> {
  const method = options.method ?? "GET";
  rejectIfSessionExpired(method, path);
  const headers: Record<string, string> = { ...clientHeaders(method, path, options), ...options.headers };
  const response = await sendWithCsrf(method, path, headers, (sent) =>
    fetch(buildUrl(path, options.query), { method, headers: sent, credentials: "include", signal: options.signal }),
  );
  if (!response.ok) return finishResponse(path, z.never(), response);
  return { blob: await response.blob(), filename: filenameFrom(response) };
}

/**
 * Subida multipart (`POST /brand/assets`): sin `Content-Type` manual — el navegador fija el
 * boundary del `multipart/form-data` — pero con el mismo `X-CSRF-Token` de doble envío que
 * cualquier otra mutación (rest-api.md §Seguridad transversal).
 */
async function requestMultipart<T>(path: string, schema: ZodSchema<T>, formData: FormData, query?: RequestOptions["query"]): Promise<T> {
  rejectIfSessionExpired("POST", path);
  const headers: Record<string, string> = clientHeaders("POST", path, { query });

  const response = await sendWithCsrf("POST", path, headers, (sent) =>
    fetch(buildUrl(path, query), { method: "POST", headers: sent, credentials: "include", body: formData }),
  );

  return finishResponse(path, schema, response);
}

function isApiErrorPayload(value: unknown): value is { error: { code: string; message: string; details?: Record<string, unknown> } } {
  return typeof value === "object" && value !== null && "error" in value;
}

export const apiClient = {
  /** Petición con opciones completas (`clientId`, cuerpo binario, cancelación) para quien no encaja en los atajos. */
  send: request,
  download,
  get: <T>(path: string, schema: ZodSchema<T>, query?: RequestOptions["query"]) =>
    request(path, schema, { method: "GET", query }),
  post: <T>(
    path: string,
    schema: ZodSchema<T>,
    body?: unknown,
    query?: RequestOptions["query"],
    headers?: RequestOptions["headers"],
  ) => request(path, schema, { method: "POST", body, query, headers }),
  put: <T>(
    path: string,
    schema: ZodSchema<T>,
    body?: unknown,
    query?: RequestOptions["query"],
    headers?: RequestOptions["headers"],
  ) => request(path, schema, { method: "PUT", body, query, headers }),
  patch: <T>(
    path: string,
    schema: ZodSchema<T>,
    body?: unknown,
    query?: RequestOptions["query"],
    headers?: RequestOptions["headers"],
  ) => request(path, schema, { method: "PATCH", body, query, headers }),
  delete: <T>(
    path: string,
    schema: ZodSchema<T>,
    body?: unknown,
    query?: RequestOptions["query"],
    headers?: RequestOptions["headers"],
  ) => request(path, schema, { method: "DELETE", body, query, headers }),
  postForm: <T>(path: string, schema: ZodSchema<T>, formData: FormData, query?: RequestOptions["query"]) =>
    requestMultipart(path, schema, formData, query),
};
